import { createSlice } from '@reduxjs/toolkit';
import { getOtp, verifyOtp, resendOtp } from '../thunk/my-profile-thunk';

const initialState = {
  loading: false,
  timer: 0,
  isOtpVerified: false,
  isOpenContactUpdate: false,
  isOpenContactUpdateSuccessModal: false,
  contactUpdateMessage: '',
  isAccessDenied: false,
  OTPInfo: null,
  expiresAt: null,
  contactUpdateErrorMessage: '',
  currentPage: 'form',
  otpVerificationLocked: false,
  errorMessage: false, // for verify/ resend error state
  resendOTP: false,
  resending: false,
  isNumberOfAttemptExceeded: false,
  remainingAttempts: 0,
  isResendAttemptExceeded: false,
};

const authSlice = createSlice({
  name: 'my-profile',
  initialState,
  reducers: {
    resetMyProfileForCache() {
      return initialState;
    },
    setTimer(state, action) {
      state.timer = action.payload;
    },
    setIsOpenContactUpdate(state, action) {
      state.isOpenContactUpdate = action.payload;
    },
    setIsOpenContactUpdateSuccessModal(state, action) {
      state.isOpenContactUpdateSuccessModal = action.payload;
    },
    setContactUpdateMessage(state, action) {
      state.contactUpdateMessage = action.payload;
    },
    setIsAccessDenied(state, action) {
      state.isAccessDenied = action.payload;
    },
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    setContactUpdateErrorMessage(state, action) {
      state.contactUpdateErrorMessage = action.payload;
    },
    setOtpVerificationLocked(state, action) {
      state.otpVerificationLocked = action.payload;
    },
    reset(state) {
      // state.isOpenContactUpdate = false;
      state.loading = false;
      state.timer = 0;
      state.isOtpVerified = false;
      // state.isOpenContactUpdateSuccessModal = false;
      state.contactUpdateMessage = '';
      state.isAccessDenied = false;
      state.OTPInfo = null;
      state.expiresAt = null;
      state.contactUpdateErrorMessage = '';
      state.currentPage = 'form';
      state.otpVerificationLocked = false;
      state.errorMessage = false;
      state.resendOTP = false;
      state.resending = false;
      state.isNumberOfAttemptExceeded = false;
      state.remainingAttempts = 0;
      state.isResendAttemptExceeded = false;
    },
    setBackButton(state) {
      // add case for back button
      state.isOpenContactUpdate = false;
      state.isOpenContactUpdateSuccessModal = false;
      state.contactUpdateMessage = '';
    },
  },
  extraReducers: (builder) => {
    builder
      // OTP 1ST API
      .addCase(getOtp.pending, (state) => {
        state.loading = true;
        state.status = 'loading';
        state.error = null;
        state.timer = 0;
        state.isAccessDenied = false;
        state.contactUpdateErrorMessage = null;
      })
      .addCase(getOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.status = 'succeeded';
        state.OTPInfo = { ...action.payload?.data };
        state.timer = action.payload?.data?.resendAfter;
        state.contactUpdateErrorMessage = null;
        state.currentPage = 'otp';
        state.isAccessDenied = false;
        state.otpVerificationLocked = false;
      })
      .addCase(getOtp.rejected, (state, action) => {
        // action.payload->{
        //     "status": "error",
        //     "code": 429,
        //     "data": null,
        //     "error": "Too many OTP requests for email. Please try again later."
        // }
        state.loading = false;
        state.status = action.payload.status;
        state.error = action.payload || 'Failed to send otp';
        state.timer = 0;
        state.contactUpdateErrorMessage = action.payload.error;
        state.otpVerificationLocked = true;

        if (action.payload.data?.error) {
          state.contactUpdateErrorMessage = action.payload.data?.error;
        }
        if (action.payload.data?.error?.errors?.[0]?.message) {
          state.contactUpdateErrorMessage =
            action.payload.data?.error?.errors?.[0]?.message;
        }
      })
      // OTP VERIFY
      .addCase(verifyOtp.pending, (state) => {
        state.status = 'loading';
        state.contactUpdateErrorMessage = null;
        state.isAccessDenied = false;
        state.otpVerificationLocked = false;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.isOtpVerified = true;
        state.contactUpdateErrorMessage = null;
        state.currentPage = 'successUpdate';
        state.isOpenContactUpdateSuccessModal = true;
        state.isOpenContactUpdate = false;
        state.timer = 0;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.status = action?.payload?.status;
        state.error = action?.payload || 'Failed to send otp';
        state.contactUpdateErrorMessage = action?.payload?.error;
        state.isAccessDenied = true;

        if (action?.payload?.data?.remainingAttempts <= 0) {
          state.otpVerificationLocked = true;
          state.isNumberOfAttemptExceeded = true;
        }
        state.errorMessage = 'wrong-otp';
        if (action?.payload?.data?.remainingAttempts === 1) {
          state.errorMessage = 'last-attempt';
        }
      })
      // OTP RESEND
      .addCase(resendOtp.pending, (state) => {
        state.status = 'loading';
        state.timer = 0;
        state.contactUpdateErrorMessage = null;
        state.isAccessDenied = false;
        state.resendOTP = true;
        state.resending = true;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.timer = action.payload?.resendAfter;
        state.contactUpdateErrorMessage = null;
        state.currentPage = 'otp';
        state.resending = false;
        state.remainingAttempts = action.payload?.remainingAttempts;
        if (action.payload?.remainingAttempts === 1) {
          state.errorMessage = 'last-resend-attempt';
        }
        if (action.payload?.remainingAttempts === 0) {
          state.errorMessage = '';
          state.isResendAttemptExceeded = true;
          state.timer = 0;
        }
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.status = action.payload?.status;
        state.error = action.payload || 'Failed to send otp';
        state.timer = 0;
        state.contactUpdateErrorMessage = action.payload.error;
        state.isAccessDenied = true;
        state.resendOTP = false;
        state.resending = false;
        // action.payload=>{
        //     "status": "error",
        //      "code": 400,
        //      "message": "OTP resent limit exceeded"
        // }
        if (action.payload.data?.remainingAttempts <= 0) {
          state.otpVerificationLocked = true;
          state.isNumberOfAttemptExceeded = true;
        }
        state.errorMessage = 'wrong-otp';
        if (action.payload.data?.remainingAttempts === 1) {
          state.errorMessage = 'last-attempt';
        }
      });
  },
});

export const {
  setTimer,
  setUser,
  setIsOpenContactUpdate,
  setIsOpenContactUpdateSuccessModal,
  setContactUpdateMessage,
  setIsAccessDenied,
  setCurrentPage,
  setContactUpdateErrorMessage,
  setOtpVerificationLocked,
  reset,
  setBackButton,
  resetMyProfileForCache,
} = authSlice.actions;

export default authSlice.reducer;
